/**
 * Human-readable origin labels for a canonical patient medication.
 *
 * Origin (`source`) is a separate dimension from clinical status and review
 * status: it records where the medication information came from and never
 * changes because a prescriber later acknowledged it.
 */
const STAFF_ORIGIN_LABELS: Record<string, string> = {
  prescribed_in_house: 'Prescribed by our practice',
  recommended_in_house: 'Recommended by our practice',
  intake_form: 'Self-reported · Client History form',
  portal: 'Self-reported · Client Portal',
  self_reported: 'Self Reported',
  documented_external: 'Documented by staff · outside provider',
  pharmacy: 'Reported by pharmacy',
  insurance_history: 'Confirmed outside medication · Insurance history',
  insurance_reported: 'Insurance reported via DoseSpot',
  other: 'Other source',
};

export function isOutsideMedication(medication: {
  source?: string | null;
  vendor_prescription_id?: string | null;
  prescribing_responsibility?: string | null;
  is_in_house?: boolean;
  item_type?: string | null;
}) {
  if (medication.item_type === 'supplement' && medication.source === 'prescribed_in_house') {
    return false;
  }
  if (medication.source === 'recommended_in_house') {
    return false;
  }
  if (medication.prescribing_responsibility === 'prescribed_by_our_practice') {
    return false;
  }
  if (medication.prescribing_responsibility === 'outside') {
    return true;
  }
  return true;
}

export function classifyMedication(medication: {
  status?: string | null;
  clinical_status?: string | null;
  item_type?: string | null;
  source?: string | null;
  prescribing_responsibility?: string | null;
  is_in_house?: boolean;
}) {
  const clinicalStatus = medication.clinical_status
    || (medication.status === 'active'
      ? 'current'
      : medication.status === 'unknown'
        ? 'unknown'
        : 'past');
  const outside = isOutsideMedication(medication);
  return {
    clinicalStatus,
    prescribingResponsibility: outside ? 'outside' : 'prescribed_by_our_practice',
    isSupplement: medication.item_type === 'supplement',
    provenance: medication.source || 'unknown',
  };
}

export type MedicationLifecycle = 'current' | 'unknown' | 'past';

export function getMedicationLifecycle(medication: {
  status?: string | null;
  clinical_status?: string | null;
}): MedicationLifecycle {
  const status = medication.clinical_status
    || (medication.status === 'active'
      ? 'current'
      : medication.status === 'unknown' ? 'unknown' : 'past');
  return status === 'current' || status === 'unknown' ? status : 'past';
}

export function groupMedicationsByLifecycle<T>(medications: T[]) {
  return medications.reduce<Record<MedicationLifecycle, T[]>>((groups, medication) => {
    groups[getMedicationLifecycle(medication as {
      status?: string | null;
      clinical_status?: string | null;
    })].push(medication);
    return groups;
  }, { current: [], unknown: [], past: [] });
}

export function isRecommendedByPractice(medication: {
  item_type?: string | null;
  source?: string | null;
}) {
  return medication.item_type === 'supplement'
    && (medication.source === 'prescribed_in_house' || medication.source === 'recommended_in_house');
}

export function medicationOriginLabel(source?: string | null): string {
  if (!source) return '';
  return STAFF_ORIGIN_LABELS[source] || 'Reported outside our practice';
}

export function medicationDisplayOrigin(medication: {
  source?: string | null;
  vendor_prescription_id?: string | null;
  item_type?: string | null;
  prescribing_responsibility?: string | null;
}) {
  if (
    medication.vendor_prescription_id
    && medication.prescribing_responsibility === 'prescribed_by_our_practice'
  ) return 'DoseSpot prescription';
  if (medication.source === 'recommended_in_house') {
    return 'Recommended by our practice';
  }
  if (medication.item_type === 'supplement' && medication.source === 'prescribed_in_house') {
    return 'Recommended by our practice';
  }
  if (medication.source === 'prescribed_in_house') return 'Prescribed by our practice';
  return medicationOriginLabel(medication.source);
}
